import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Comments, CreateChancel, DateRow, DetailItem, Loader, SimpleTabs } from 'components';
import {commentModels, Images, useModal, User} from 'utils';
import { httpRequestsOnSuccessActions, wishlistActions } from 'store';
import { WISHLIST_COMMENT_POSTED } from 'store/wishlist/wishlist.types';
import { WishlistForm } from './wishlistForm';
import { WishlistAttachmentList } from './wishlistAttachments';
import { CategoryChip, isOwnRequest, StatusChip, WISHLIST_ATTACHMENT_PURPOSE } from '../constants';
import infoGrayIcon from "../../../assets/images/icons/infoGrayIcon.svg";

const TABS = { DETAILS: 'Details', COMMENTS: 'Comments' };

export const WishlistDetails = ({ id, requestParams, startInEdit = false }) => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const { userInfo } = User();
    const item = useSelector((state) => state.wishlist.wishlistItem);
    const [tab, setTab] = useState(TABS.DETAILS);
    const [editing, setEditing] = useState(startInEdit);

    useEffect(() => {
        dispatch(wishlistActions.getWishlistItemById(id));
    }, [id]);

    // Until the fetch for THIS id lands, the store may still hold the last
    // request that was opened — never render that one.
    const current = item?.id === id ? item : null;
    const canEdit = isOwnRequest(current, userInfo?.id);

    if (!current) {
        return (
            <div className="wishlist-details">
                <Loader height={300} />
            </div>
        );
    }

    const details =
        editing && canEdit ? (
            <WishlistForm
                fromEdit={true}
                info={current}
                requestParams={requestParams}
                onCancel={() => setEditing(false)}
                afterSuccess={() => setEditing(false)}
            />
        ) : (
            <div>
                <DetailItem title="Request ID" value={current.requestNumber} />
                <DetailItem title="Requested By" value={current.createdBy?.name || '--'} />
                <DetailItem title="Submitted" value={<DateRow date={current.createdAt} />} />
                <DetailItem title="Status" value={<StatusChip status={current.status} />} />
                <DetailItem title="Proposed Completion" value={<DateRow date={current.proposedCompletionDate} />} />
                <DetailItem title="Title" value={current.title} />
                <DetailItem title="Description" value={current.description} wrapperStyles={{ whiteSpace: 'pre-wrap' }} />

                {current.attachments?.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <p className="modal-header-sub">Attachments</p>
                        <WishlistAttachmentList files={current.attachments} />
                    </div>
                )}


                <div className="wish-list-edit-information">
                    <img src={Images.infoCircle} alt="info" />
                    <p> Status and proposed completion date are set by the TherapyLake team.</p>
                </div>

                {/*<div className='flex-align-start'>*/}
                {/*    <img src={Images.infoGrayIcon} alt="incoIcon"/>*/}
                {/*</div>*/}
                {/*<p className="modal-header-sub" style={{ margin: '16px 0 24px' }}>*/}
                {/*   */}
                {/*</p>*/}

                {canEdit ? (
                    <CreateChancel create="Edit" chancel="Cancel" onCreate={() => setEditing(true)} onClose={close} buttonWidth="100%" />
                ) : (
                    <CreateChancel create="Close" onCreate={close} noChancel buttonWidth="100%" />
                )}
            </div>
        );

    const comments = (
        <Comments
            resource={commentModels.Wishlist}
            resourceId={current.id}
            fromDetail
            width="100%"
            height={520}
            enableMentions={false}
            attachmentPurpose={WISHLIST_ATTACHMENT_PURPOSE}
            confirmInSecondaryModal
            afterSuccess={() => {
                dispatch(httpRequestsOnSuccessActions.appendSuccess(WISHLIST_COMMENT_POSTED));
                // Both carry the comment count: the tab label and the row badge.
                dispatch(wishlistActions.getWishlistItemById(current.id));
                dispatch(wishlistActions.getWishlistItems(requestParams, 'noLoad'));
            }}
        />
    );

    return (
        <div className="wishlist-details">
            <p className="modal-header-title">Request {current.requestNumber}</p>
            <div className="flex-align-center" style={{ gap: 8, margin: '8px 0 16px' }}>
                <CategoryChip category={current.category} />
                <StatusChip status={current.status} />
            </div>

            {/* `getTab` is required: without it SimpleTabs writes to location.state
                and would wipe the list's filters behind the modal. */}
            <SimpleTabs
                getTab={(t) => {
                    setTab(t);
                    setEditing(false);
                }}
                tab={tab}
                defaultTab={TABS.DETAILS}
                tabsLabels={[
                    { label: TABS.DETAILS },
                    // An explicit `text` keeps a text label — 'Comments' alone renders as an icon.
                    {
                        label: TABS.COMMENTS,
                        text: current.commentCount ? `Comments (${current.commentCount})` : 'Comments',
                    },
                ]}
                tabsContent={[
                    { tab: TABS.DETAILS, tabComponent: details },
                    { tab: TABS.COMMENTS, tabComponent: comments },
                ]}
                customTabPanel="customTabPanel"
            />
        </div>
    );
};
